import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteProformaGenerationMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

export function ProformaInvoiceGenerationOverview() {
  const navigate = useNavigate();
  const [deleteProformaGeneration] = useDeleteProformaGenerationMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;
    const branchName = getColumnData<{label: string}>('branch')?.value?.label;

    match(actionKey)
      .with('DETAIL', 'EDIT', () =>
        navigate(composePath(settingsRoutes.proformaGenerationDetail, {params: {id}}))
      )
      .with('DELETE', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteProformaGeneration({id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
          text: i18n.t('entity.accounting.labels.deleteDefinitionDeleteText', {
            name: branchName,
          }),
        })
      );
  };

  const title = i18n.t('page.accountingSettings.labels.proformaGeneration');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              title: i18n.t('page.accounting.labels.newDefinition'),
              onClick: () => navigate(settingsRoutes.proformaGenerationNew),
            },
          ],
        }}
        description={i18n.t('page.accountingSettings.labels.proformaGenerationDescription')}
        data-testid={testIds.settings.proformaGeneration('page')}
      >
        <DmsDataGrid
          gridCode="accounting-business-case-proforma-invoice-settings"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.proformaGeneration('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
